<template>
  <div class="modern-color-theme font-poppins flex flex-col gap-2" data-component-name="VTextArea">
    <VSLabel v-if="props.label" :tooltip="props.labelTooltip" :for="id">{{ props.label }}</VSLabel>
    <div :class="{ 'relative': props.maxLength }">
      <textarea
        :id="id"
        v-model="modelValueInternal"
        :disabled="props.disabled"
        :rows="props.rows"
        :readonly="props.readonly"
        :placeholder="props.placeholder"
        :maxlength="props.maxLength"
        :name="props.name"
        class="block w-full rounded-md border-0 py-1.5 text-neutral-950 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-500 focus:ring-2 focus:ring-inset focus:ring-blue-600 text-sm leading-6"
        :class="computedInputClass"
        :aria-describedby="descriptionId"
        @keydown="(event) => { handleKeyDown(event); scheduleFlush() }"
        @paste="handlePaste"
        @blur="flush"
      />
      <div v-if="props.maxLength" class="absolute bottom-0 right-0 p-2 pr-3 whitespace-nowrap pointer-events-none text-xs leading-4 font-normal" :class="{ 'text-neutral-500': !props.disabled, 'text-neutral-250': props.disabled }" role="status" aria-live="polite">
        {{ modelValueInternalLength }} / {{ props.maxLength }}
      </div>
    </div>
    <VSValidation v-if="validationResult" :type="validationResult[0]" :message="validationResult[1]" />
    <VSDescription v-if="props.description" :id="descriptionId" v-safe-html="props.description" />
  </div>
</template>
<script lang="ts" setup>
import { computed } from 'vue';
import VSDescription from './components/VSDescription.vue'
import VSLabel from './components/VSLabel.vue'
import { useElementId, useDescriptionElementId } from '../../utils/utils';
import { useBuffering } from './helpers/buffering';
import { handleEvents } from './helpers/input';
import { SHARED_PROPS_DEFAULTS, VALIDATION_PROPS_DEFAULTS, type BufferingProps, type LengthRestrictionProps, type PlaceholderProps, type SharedProps, type ValidationProps } from './helpers/types';
import VSValidation from './components/VSValidation.vue';
import { createDefaultValidator, useValidation } from './helpers/validations';
import type { ThemeProp } from '@library/themes/types';
import { useTheme } from '@library/themes/themes';

defineOptions({
  name: 'VTextArea'
})

const props = withDefaults(
  defineProps<SharedProps & ValidationProps<string> & LengthRestrictionProps & PlaceholderProps & BufferingProps & {
    whitelist?: string[]
    required?: boolean
    rows?: number
    theme?: ThemeProp<'VTextArea'>
  }>(),
  {
    ...SHARED_PROPS_DEFAULTS,
    ...VALIDATION_PROPS_DEFAULTS,
    placeholder: undefined,
    required: false,
    whitelist: undefined,
    rows: undefined,
    theme: undefined
  }
)

const id = useElementId(props.id)
const descriptionId = useDescriptionElementId(props.description, id.value)

const modelValue = defineModel<string | null | undefined>({ required: true })

const { intermediateModel: modelValueInternal, scheduleFlush, flush } = useBuffering(modelValue, props)

const modelValueInternalLength = computed(() => modelValueInternal.value?.length ?? 0)

const theme = useTheme(props, 'VTextArea')

const { validationVisible, validationResult, isValid, isValidationError, isValidationWarning } = useValidation(
  modelValueInternal,
  props,
  createDefaultValidator(props)
)

defineExpose({
  isValid,
  get isEmpty () {
    return !modelValueInternal.value
  }
})

const computedInputClass = computed(() => ({
  'opacity-50 cursor-not-allowed': props.disabled,
  [theme.value.content.disabled]: props.disabled,
  [theme.value.content.readonly]: !props.disabled && props.readonly,
  [theme.value.content.enabled]: !props.disabled && !props.readonly,
  'ring-red-300': isValidationError.value,
  'ring-yellow-300': isValidationWarning.value
}))

const { handleKeyDown, handlePaste } = handleEvents(props, validationVisible)
</script>